import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppear } from '../hooks/useAppear';
import Img from '../components/ui/Img';
import { programmes } from '../data';
import {
  IcoHome, IcoActivity, IcoShield, IcoUsers,
  IcoHeart, IcoBook, IcoLeaf, IcoAward, IcoTarget,
} from '../assets/icons';

const progIcons = {
  pdic:          IcoHome,
  tata:          IcoActivity,
  geracao:       IcoShield,
  'seja-mulher': IcoUsers,
  dikota:        IcoHeart,
  campus:        IcoBook,
  revieng:       IcoLeaf,
  'pe-boto':     IcoAward,
};

const progPhotos = {
  pdic:
    'https://jpumokwyaqpqmqubhaep.supabase.co/storage/v1/object/public/franstel_armazenamento/fotos%20pdic/_DSC9067.JPG',
  tata:
    'https://jpumokwyaqpqmqubhaep.supabase.co/storage/v1/object/public/franstel_armazenamento/FOTOS%20TATA%20UHAYELE/_DSC1426%20copiar.jpg',
  geracao:
    'https://jpumokwyaqpqmqubhaep.supabase.co/storage/v1/object/public/franstel_armazenamento/FOTOS%20TATA%20UHAYELE/_DSC1432.JPG',
  'seja-mulher':
    'https://jpumokwyaqpqmqubhaep.supabase.co/storage/v1/object/public/franstel_armazenamento/img_fdc/ff.jpg.jpeg',
  dikota:
    'https://jpumokwyaqpqmqubhaep.supabase.co/storage/v1/object/public/franstel_armazenamento/FOTOS%20TATA%20UHAYELE/02.jpg',
  campus:
    'https://jpumokwyaqpqmqubhaep.supabase.co/storage/v1/object/public/franstel_armazenamento/img_fdc/OIF.jpg',
  revieng:
    'https://jpumokwyaqpqmqubhaep.supabase.co/storage/v1/object/public/franstel_armazenamento/img_fdc/DD.jpg',
  'pe-boto':
    'https://jpumokwyaqpqmqubhaep.supabase.co/storage/v1/object/public/franstel_armazenamento/img_fdc/D%20(2).jpg',
};

/* ─── Single Programme Card ──────────────────────────────────────────────── */
function ProgramCard({ prog, isPt, isOpen, onToggle }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.classList.add('appear');
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { el.classList.add('show'); obs.unobserve(el); } },
      { threshold: 0.08 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const Icon = progIcons[prog.id] || IcoTarget;
  const photo = progPhotos[prog.id] || null;

  return (
    <article
      ref={ref}
      id={prog.id}
      className={`
        group rounded-2xl overflow-hidden transition-all duration-300 border
        ${isOpen
          ? 'border-brand-200 dark:border-brand-800 shadow-xl shadow-brand-900/6 col-span-full'
          : 'border-brand-100 dark:border-brand-900 bg-white dark:bg-brand-950 hover:shadow-md hover:shadow-brand-900/5 hover:border-brand-200 dark:hover:border-brand-800'}
      `}
    >
      {/* ── Collapsed: horizontal summary ── */}
      {!isOpen && (
        <button
          onClick={onToggle}
          className="w-full flex items-center gap-5 p-6 text-left"
        >
          {/* Coloured photo thumb */}
          <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 bg-brand-100 dark:bg-brand-900">
            {photo && (
              <Img
                src={photo}
                alt={isPt ? prog.title_pt : prog.title_en}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
            )}
          </div>

          <div className="flex-1 min-w-0">
            <span className="text-[10px] font-bold tracking-[0.14em] uppercase text-brand-500 block mb-0.5">
              {prog.acronym}
            </span>
            <h3 className="text-[15px] font-bold text-brand-900 dark:text-white leading-snug truncate">
              {isPt ? prog.title_pt : prog.title_en}
            </h3>
            <p className="text-[13px] text-brand-500 mt-0.5 truncate">
              {isPt ? prog.subtitle_pt : prog.subtitle_en}
            </p>
          </div>

          {/* Icon pill */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="hidden sm:block text-[11px] font-semibold text-brand-500 group-hover:text-brand-700 dark:group-hover:text-brand-300 transition-colors">
              {isPt ? 'Ver mais' : 'Learn more'}
            </span>
            <div className="w-8 h-8 rounded-full border border-brand-200 dark:border-brand-800 flex items-center justify-center text-brand-500 group-hover:bg-brand-600 group-hover:border-brand-600 group-hover:text-white transition-all duration-200">
              <svg className="w-3.5 h-3.5" viewBox="0 0 14 14" fill="none">
                <path d="M7 1v12M1 7h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </div>
          </div>
        </button>
      )}

      {/* ── Expanded: full layout ── */}
      {isOpen && (
        <div>
          {/* Top image strip */}
          <div className="relative h-64 sm:h-80 overflow-hidden">
            {photo && (
              <Img
                src={photo}
                alt={isPt ? prog.title_pt : prog.title_en}
                className="w-full h-full object-cover"
              />
            )}
            {/* Overlay gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-brand-950/80 via-brand-950/20 to-transparent" />

            {/* Overlaid title */}
            <div className="absolute bottom-0 left-0 right-0 p-8 flex items-end justify-between">
              <div>
                <span className="inline-block text-[10px] font-bold tracking-[0.16em] uppercase text-brand-300 mb-2">
                  {prog.acronym}
                </span>
                <h3 className="text-2xl sm:text-3xl font-bold text-white leading-tight max-w-lg">
                  {isPt ? prog.title_pt : prog.title_en}
                </h3>
              </div>
              <button
                onClick={onToggle}
                className="flex-shrink-0 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 flex items-center justify-center text-white hover:bg-white/30 transition-all"
                aria-label="Fechar"
              >
                <svg className="w-4 h-4" viewBox="0 0 14 14" fill="none">
                  <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
          </div>

          {/* Content body */}
          <div className="bg-white dark:bg-brand-950 p-8">
            <div className="grid sm:grid-cols-3 gap-10">

              {/* Left col: description + actions */}
              <div className="sm:col-span-2">
                <p className="text-[15px] text-brand-700 dark:text-brand-300 leading-relaxed mb-8">
                  {isPt ? prog.desc_pt : prog.desc_en}
                </p>

                <h4 className="text-[10px] font-bold tracking-[0.14em] uppercase text-brand-400 mb-4">
                  {isPt ? 'Acções Desenvolvidas' : 'Actions Undertaken'}
                </h4>
                <div className="grid sm:grid-cols-2 gap-3">
                  {(isPt ? prog.actions_pt : prog.actions_en).map((a, i) => (
                    <div
                      key={i}
                      className="flex items-start gap-3 p-3.5 rounded-xl bg-brand-50 dark:bg-brand-900/40 border border-brand-100 dark:border-brand-900"
                    >
                      <span className="w-5 h-5 rounded-full bg-brand-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 10 10" fill="none">
                          <path d="M2 5.5l2 2 4-4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                      <span className="text-[13px] text-brand-700 dark:text-brand-400 leading-relaxed">{a}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right col: metadata */}
              <div className="space-y-6">
                {/* Areas */}
                <div>
                  <h4 className="text-[10px] font-bold tracking-[0.14em] uppercase text-brand-400 mb-3">
                    {isPt ? 'Áreas de Actuação' : 'Areas of Action'}
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {(isPt ? prog.areas_pt : prog.areas_en).map((a, i) => (
                      <span
                        key={i}
                        className="text-[11px] font-semibold px-3 py-1 rounded-full bg-brand-50 dark:bg-brand-900/40 border border-brand-200 dark:border-brand-800 text-brand-700 dark:text-brand-400"
                      >
                        {a}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Location */}
                {prog.province_pt && (
                  <div>
                    <h4 className="text-[10px] font-bold tracking-[0.14em] uppercase text-brand-400 mb-3">
                      {isPt ? 'Localização' : 'Location'}
                    </h4>
                    <div className="flex items-center gap-2.5 p-3.5 rounded-xl bg-brand-50 dark:bg-brand-900/40 border border-brand-100 dark:border-brand-900">
                      <svg className="w-4 h-4 text-brand-600 flex-shrink-0" viewBox="0 0 16 16" fill="none">
                        <path d="M8 1.5A4.5 4.5 0 0113.5 6c0 3-4.5 8.5-4.5 8.5S4.5 9 4.5 6A4.5 4.5 0 018 1.5z" stroke="currentColor" strokeWidth="1.3"/>
                        <circle cx="8" cy="6" r="1.5" stroke="currentColor" strokeWidth="1.3"/>
                      </svg>
                      <span className="text-[13px] font-medium text-brand-700 dark:text-brand-400">
                        {isPt ? prog.province_pt : prog.province_en}
                      </span>
                    </div>
                  </div>
                )}

                {/* Icon badge */}
                <div className="flex items-center gap-3 pt-2">
                  <div className="w-10 h-10 rounded-xl bg-brand-600 flex items-center justify-center">
                    <Icon cls="w-5 h-5 text-white" />
                  </div>
                  <span className="text-[11px] font-bold tracking-[0.12em] uppercase text-brand-500">
                    {prog.acronym}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </article>
  );
}

/* ─── Page ───────────────────────────────────────────────────────────────── */
export default function ProgrammesPage() {
  const { i18n } = useTranslation();
  const isPt = i18n.language === 'pt';
  const [openId, setOpenId] = useState(null);
  const heroRef = useAppear();

  const toggle = (id) => setOpenId((o) => (o === id ? null : id));

  const pdic8Photos = [
    'https://jpumokwyaqpqmqubhaep.supabase.co/storage/v1/object/public/franstel_armazenamento/fotos%20pdic/_DSC9067.JPG',
    'https://jpumokwyaqpqmqubhaep.supabase.co/storage/v1/object/public/franstel_armazenamento/fotos%20pdic/_DSC9090.JPG',
    'https://jpumokwyaqpqmqubhaep.supabase.co/storage/v1/object/public/franstel_armazenamento/fotos%20pdic/_DSC9449_1.JPG',
    'https://jpumokwyaqpqmqubhaep.supabase.co/storage/v1/object/public/franstel_armazenamento/fotos%20pdic/_DSC9454_1.JPG',
    'https://jpumokwyaqpqmqubhaep.supabase.co/storage/v1/object/public/franstel_armazenamento/fotos%20pdic/_DSC9456_1.JPG',
    'https://jpumokwyaqpqmqubhaep.supabase.co/storage/v1/object/public/franstel_armazenamento/fotos%20pdic/_DSC9457_1.JPG',
  ];

  const pdillarsPt = [
    { t: 'Infra-estruturas',  d: 'Construção e reabilitação de equipamentos sociais.' },
    { t: 'Água Potável',      d: 'Sistemas de abastecimento de água nas comunidades.' },
    { t: 'Agricultura',       d: 'Distribuição de sementes e árvores frutíferas.' },
    { t: 'Feiras de Saúde',   d: 'Assistência médica itinerante e rastreios.' },
    { t: 'Educação',          d: 'Formação comunitária e técnico-profissional.' },
    { t: 'Saneamento',        d: 'Campanhas de higiene, saneamento e ambiente.' },
  ];
  const pdillarsEn = [
    { t: 'Infrastructure',  d: 'Construction and rehabilitation of social facilities.' },
    { t: 'Clean Water',     d: 'Water supply systems in communities.' },
    { t: 'Agriculture',     d: 'Distribution of seeds and fruit trees.' },
    { t: 'Health Fairs',    d: 'Itinerant medical assistance and screenings.' },
    { t: 'Education',       d: 'Community and technical-vocational training.' },
    { t: 'Sanitation',      d: 'Hygiene, sanitation and environmental campaigns.' },
  ];
  const pillars = isPt ? pdillarsPt : pdillarsEn;

  return (
    <main>

      {/* ═══ HERO ═══════════════════════════════════════════════════════════ */}
      <section className="bg-white dark:bg-brand-950 pt-[68px] overflow-hidden text-center">
        <div ref={heroRef} className="wrap pt-16 pb-0 max-w-3xl mx-auto">

          {/* Overline */}
          <p className="text-[11px] font-semibold tracking-[0.08em] uppercase text-brand-600 mb-4">
            {isPt ? 'Fundação · 8 Programas Activos' : 'Foundation · 8 Active Programmes'}
          </p>

          <h1 className="display-xl text-brand-900 dark:text-white mb-5 tracking-tight leading-[1.05]">
            {isPt ? 'Programas & ' : 'Programmes & '}
            <span className="text-brand-600">{isPt ? 'Projectos' : 'Projects'}</span>
          </h1>

          <p className="body-lg text-brand-500 dark:text-brand-400 max-w-lg mx-auto leading-relaxed mb-12">
            {isPt
              ? 'Oito programas que transformam comunidades angolanas, da saúde à educação, do ambiente ao desporto.'
              : 'Eight programmes transforming Angolan communities from health to education, environment to sport.'}
          </p>

          {/* Hero image rising from bottom */}
          <div className="rounded-t-2xl overflow-hidden shadow-2xl shadow-brand-900/10 max-w-4xl mx-auto">
            <Img
              src="https://jpumokwyaqpqmqubhaep.supabase.co/storage/v1/object/public/franstel_armazenamento/fotos%20pdic/_DSC9449_1.JPG"
              alt={isPt ? 'Programas e Projectos' : 'Programmes and Projects'}
              className="w-full h-72 sm:h-96 object-cover object-center"
            />
          </div>
        </div>
      </section>

      {/* ═══ PROGRAMMES LIST ════════════════════════════════════════════════ */}
      <section className="section-pad bg-brand-50 dark:bg-brand-900/10">
        <div className="wrap">

          {/* Section label */}
          <div className="flex items-center gap-4 mb-8">
            <span className="text-[11px] font-bold tracking-[0.16em] uppercase text-brand-500">
              {isPt ? 'Todos os Programas' : 'All Programmes'}
            </span>
            <span className="flex-1 h-px bg-brand-200 dark:bg-brand-800" />
          </div>

          {/* List */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 [&_.col-span-full]:lg:col-span-2">
            {programmes.map((prog) => (
              <ProgramCard
                key={prog.id}
                prog={prog}
                isPt={isPt}
                isOpen={openId === prog.id}
                onToggle={() => toggle(prog.id)}
              />
            ))}
          </div>

        </div>
      </section>

      {/* ═══ PDIC FEATURE ═══════════════════════════════════════════════════ */}
      <section className="section-pad bg-white dark:bg-brand-950">
        <div className="wrap">

          {/* Header row */}
          <div className="grid lg:grid-cols-2 gap-12 items-end mb-14">
            <div>
              <div className="flex items-center gap-3 mb-5">
                <span className="h-px w-8 bg-brand-600" />
                <span className="text-[11px] font-bold tracking-[0.16em] uppercase text-brand-600">
                  {isPt ? 'Projecto Piloto em Destaque' : 'Featured Pilot Project'}
                </span>
              </div>
              <h2 className="display-md text-brand-900 dark:text-white leading-tight mb-5">
                {isPt ? 'PDIC — Comunidade\ndo Toco, Huíla' : 'PDIC — Toco Community,\nHuíla'}
              </h2>
              <p className="body-md text-brand-600 dark:text-brand-400 leading-relaxed">
                {isPt
                  ? 'Modelo inovador de desenvolvimento participativo onde a própria população é activamente envolvida na transformação da sua realidade social.'
                  : 'Innovative participatory development model where the population itself is actively involved in transforming its social reality.'}
              </p>
            </div>

            {/* Pillar grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {pillars.map((p, i) => (
                <div
                  key={i}
                  className="p-4 rounded-xl border border-brand-100 dark:border-brand-900 bg-brand-50 dark:bg-brand-900/30"
                >
                  <div className="w-5 h-0.5 bg-brand-600 mb-3 rounded-full" />
                  <p className="text-[13px] font-bold text-brand-900 dark:text-white mb-1">{p.t}</p>
                  <p className="text-[11px] text-brand-500 leading-relaxed">{p.d}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Photo mosaic */}
          <div className="grid grid-cols-3 grid-rows-2 gap-3 h-[480px]">
            {/* Large left */}
            <div className="row-span-2 rounded-2xl overflow-hidden">
              <Img
                src={pdic8Photos[0]}
                alt="PDIC Toco"
                className="w-full h-full object-cover hover:scale-103 transition-transform duration-700"
              />
            </div>
            {/* Top middle */}
            <div className="rounded-2xl overflow-hidden">
              <Img src={pdic8Photos[1]} alt="PDIC Toco" className="w-full h-full object-cover hover:scale-103 transition-transform duration-700" />
            </div>
            {/* Top right */}
            <div className="rounded-2xl overflow-hidden">
              <Img src={pdic8Photos[2]} alt="PDIC Toco" className="w-full h-full object-cover hover:scale-103 transition-transform duration-700" />
            </div>
            {/* Bottom middle */}
            <div className="rounded-2xl overflow-hidden">
              <Img src={pdic8Photos[3]} alt="PDIC Toco" className="w-full h-full object-cover hover:scale-103 transition-transform duration-700" />
            </div>
            {/* Bottom right — with caption overlay */}
            <div className="relative rounded-2xl overflow-hidden">
              <Img src={pdic8Photos[4]} alt="PDIC Toco" className="w-full h-full object-cover hover:scale-103 transition-transform duration-700" />
              <div className="absolute inset-0 bg-brand-950/50 flex items-end p-4">
                <p className="text-white text-[11px] font-semibold leading-tight">
                  {isPt ? 'Comunidade do Toco · Huíla' : 'Toco Community · Huíla'}
                </p>
              </div>
            </div>
          </div>

        </div>
      </section>

    </main>
  );
}